import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ExpandIcon, X } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogClose } from '~/components/ui/dialog';
import { cn } from '~/lib/utils';
import { getImageUrl, WORKSPACE_DETAIL_IMAGE_SIZE } from '~/utils/misc';
import { Image } from './image';
import { ImageTransforms } from '~/.server/db/schema';

type WorkspaceImage = {
  id: string;
  slug: string;
  altText: string | null;
  workspaceId: string;
  transforms?: ImageTransforms | null;
};

type CarouselProps = {
  images: WorkspaceImage[];
  title: string;
};

const WorkspaceCarousel = ({ images, title }: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const showNext = () => {
    setCurrentIndex((current) => (current + 1) % images.length);
  };

  const showPrevious = () => {
    setCurrentIndex((current) => (current - 1 + images.length) % images.length);
  };

  const handleImageClick = (index: number) => {
    setCurrentIndex(index);
    setIsFullscreen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      showPrevious();
    } else if (e.key === 'ArrowRight') {
      showNext();
    }
  };

  if (!images.length) return null;

  return (
    <>
      <section 
        className="relative w-full space-y-2" 
        aria-label="Image carousel"
      >
        {/* Keyboard Controls */}
        <Button
          variant="ghost"
          className="sr-only focus:not-sr-only"
          onKeyDown={handleKeyDown}
        >
          Use arrow keys to navigate images
        </Button>

        {/* Main Image - 16:9 aspect ratio */}
        <div className="relative w-full">
          <Button
            variant="ghost"
            className="p-0 h-auto w-full relative block"
            onClick={() => handleImageClick(0)}
          >
            <div className="relative w-full aspect-video overflow-hidden rounded-lg bg-muted">
              <Image
                src={getImageUrl(images[currentIndex]?.slug, WORKSPACE_DETAIL_IMAGE_SIZE)}
                alt={images[currentIndex]?.altText || title}
                containerClassName="absolute inset-0"
                className="w-full h-full object-cover rounded-lg"
                rotation={images[currentIndex]?.transforms?.rotation || 0}
                scaleOnRotate
                priority
              />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors rounded-lg pointer-events-none">
                <ExpandIcon className="absolute top-4 right-4 h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </Button>
        </div>

        {/* Thumbnails Grid */}
        {images.length > 1 && (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 overflow-x-auto">
            {images.map((image, index) => (
              <Button
                key={image.id}
                variant="ghost"
                className={cn(
                  "p-0 h-auto w-full relative block group",
                  currentIndex === index && "ring-2 ring-primary ring-offset-2"
                )}
                onClick={() => setCurrentIndex(index)}
                aria-label={`View image ${index + 1} of ${images.length}`}
                aria-selected={currentIndex === index}
              >
                <div className="relative aspect-video overflow-hidden rounded-md">
                  <Image
                    src={getImageUrl(image?.slug, WORKSPACE_DETAIL_IMAGE_SIZE)}
                    alt={image.altText || `${title} - Image ${index + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    rotation={image?.transforms?.rotation || 0}
                    scaleOnRotate
                    priority={index < 8}
                  />
                  <div className={cn(
                    "absolute inset-0 rounded-md",
                    currentIndex === index ? "bg-black/20" : "bg-black/0 group-hover:bg-black/10",
                    "transition-colors"
                  )} />
                </div>
              </Button>
            ))}
          </div>
        )}

        {/* Mobile Indicator */}
        <div className="flex justify-center gap-1.5 md:hidden">
          {images.map((_, index) => (
            <Button
              key={index}
              variant="ghost"
              size="icon"
              className={cn(
                "w-1.5 h-1.5 p-0 rounded-full",
                currentIndex === index ? "bg-primary" : "bg-gray-300"
              )}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to image ${index + 1}`}
              aria-selected={currentIndex === index}
            />
          ))}
        </div>
      </section>

      {/* Fullscreen Dialog */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-[95vw] w-full h-[90vh] p-0">
          <div className="relative h-full bg-black/95">
            {/* Close button */}
            <DialogClose className="absolute right-4 top-4 z-10">
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-full bg-black/50 hover:bg-black/70"
                aria-label="Close dialog"
              >
                <X className="h-6 w-6 text-white" />
              </Button>
            </DialogClose>

            {/* Main image */}
            <div className="h-full flex items-center justify-center p-4 overflow-hidden">
              <Image
                src={getImageUrl(images[currentIndex]?.slug, WORKSPACE_DETAIL_IMAGE_SIZE)}
                alt={images[currentIndex]?.altText || `${title} - Image ${currentIndex + 1}`}
                className="max-h-full max-w-full object-contain"
                rotation={images[currentIndex]?.transforms?.rotation || 0}
                scaleOnRotate
              />
            </div>
            
            {/* Navigation Buttons */}
            {images.length > 1 && (
              <div className="absolute inset-y-0 left-4 right-4 flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    showPrevious();
                  }}
                  className="h-12 w-12 rounded-full bg-black/50 hover:bg-black/70"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-8 w-8 text-white" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    showNext();
                  }}
                  className="h-12 w-12 rounded-full bg-black/50 hover:bg-black/70"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-8 w-8 text-white" />
                </Button>
              </div>
            )}

            {/* Image Counter */}
            <div 
              className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full"
              role="status"
              aria-live="polite"
            >
              {currentIndex + 1} / {images.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};


export default WorkspaceCarousel;
